import React, { useEffect, useRef, useState } from "react";
import {
  StyledSwitchBookmark,
  StyledTextSwitch,
  StyledTextSwitchItem,
} from "./TextSwitch.styled";
import { BodyTyp } from "../Typography/Typography";
import { FontWeightEnum } from "../../types/AppTypes";
import { theme } from "../../constants/StyledComponents";

export type OptionItem<T, L extends React.ReactNode = string> = {
  value: T;
  label: L;
};

export enum TextSwitchVariantEnum {
  ROUNDED = "rounded",
  SQUARED = "squared",
}

type TextSwitchProps<T, L extends React.ReactNode = string> = {
  options: OptionItem<T, L>[];
  selectedOption: OptionItem<T, L> | null;
  setSelectedOption: (x: OptionItem<T, L>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: TextSwitchVariantEnum;
};

export const TextSwitch = <T, L extends React.ReactNode = string>({
  options,
  selectedOption,
  setSelectedOption,
  disabled = false,
  fullWidth = false,
  variant = TextSwitchVariantEnum.ROUNDED,
}: TextSwitchProps<T, L>) => {
  const [bookmarkStyle, setBookmarkStyle] = useState<{
    width: number;
    transform: string;
  }>({ width: 0, transform: "translateX(0px)" });
  const [allowTransitions, setAllowTransitions] = useState(false);

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (selectedOption && itemRefs.current.length > 0) {
      const selectedIndex = options.findIndex(
        (opt) => opt.value === selectedOption.value,
      );
      const selectedElement = itemRefs.current[selectedIndex];

      if (selectedElement) {
        const { offsetWidth, offsetLeft } = selectedElement;
        setBookmarkStyle({
          width: offsetWidth,
          transform: `translateX(${offsetLeft}px)`,
        });
        setTimeout(() => setAllowTransitions(true), 0);
      }
    }
  }, [selectedOption, options]);

  const isSelected = (option: OptionItem<T, L>): boolean => {
    return selectedOption !== null && selectedOption.value === option.value;
  };

  return (
    <StyledTextSwitch
      $disabled={disabled}
      $fullWidth={fullWidth}
      $variant={variant}
    >
      <StyledSwitchBookmark
        style={bookmarkStyle}
        $allowTransitions={allowTransitions}
        $variant={variant}
      />
      {options.map((option, index) => {
        const isString = typeof option.label === "string";
        return (
          <StyledTextSwitchItem
            $isActive={isSelected(option)}
            $isString={isString}
            $fullWidth={fullWidth}
            $disabled={disabled}
            key={`option-${option.value}`}
            onClick={disabled ? () => {} : () => setSelectedOption(option)}
            ref={(el) => (itemRefs.current[index] = el)}
          >
            {isString ? (
              <BodyTyp
                color={
                  isSelected(option)
                    ? theme.colors.white
                    : disabled
                      ? theme.colors.grey_3
                      : theme.colors.black
                }
                variant="3"
                fontWeight={
                  isSelected(option)
                    ? FontWeightEnum.SEMIBOLD
                    : FontWeightEnum.MEDIUM
                }
              >
                {option.label}
              </BodyTyp>
            ) : (
              <>{option.label}</>
            )}
          </StyledTextSwitchItem>
        );
      })}
    </StyledTextSwitch>
  );
};
